/**
 * Slider - Reusable slider component for numeric input controls.
 *
 * ARCHITECTURE:
 * - Provides a styled slider input with customizable range and styling.
 * - Supports controlled component pattern with value and onChange props.
 * - Integrates with the existing theme system for consistent styling.
 *
 * RESPONSIBILITIES:
 * - Render a styled range slider input.
 * - Handle value changes and invoke callbacks.
 * - Provide accessible and responsive slider controls.
 */

import React, { useRef, useState } from 'react'
import {
  SliderContainer,
  StyledSlider,
  MiddleIndicator,
  SliderTrackBefore,
  SliderTrackAfter,
} from './styles/Slider.styles'

interface SnapRange {
  snapStart: number
  snapEnd: number
  goTo: number
}

interface SliderProps {
  value: number
  onChange?: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  useMiddleIndicator?: boolean
  trackWidth?: number
  snap?: SnapRange[]
  className?: string
  // Allow any additional HTML input attributes except onChange
  [key: string]: any
}

const Slider: React.FC<SliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  useMiddleIndicator = true,
  trackWidth = 16, // Default 1rem (16px)
  snap = [], // Array of snap objects: [{snapStart: float, snapEnd: float, goTo: float}, ...]
  className,
  ...props
}) => {
  const sliderRef = useRef(null)
  const [isHovered, setIsHovered] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      let newValue = parseFloat(e.target.value)

      // Check if the new value falls within any snap range
      if (snap && snap.length > 0) {
        for (const snapRange of snap) {
          const { snapStart, snapEnd, goTo } = snapRange

          // Ensure snapStart <= snapEnd for proper range checking
          const rangeStart = Math.min(snapStart, snapEnd)
          const rangeEnd = Math.max(snapStart, snapEnd)

          // If value is within the snap range, snap to goTo value
          if (newValue >= rangeStart && newValue <= rangeEnd) {
            newValue = goTo
            break // Stop checking other ranges once we find a match
          }
        }
      }

      onChange(newValue)
    }
  }

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true)
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <SliderContainer
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      trackWidth={trackWidth}
      disabled={disabled}
    >
      <SliderTrackBefore
        isHovered={isHovered}
        trackWidth={trackWidth}
        disabled={disabled}
      />
      {useMiddleIndicator && <MiddleIndicator disabled={disabled} />}
      <StyledSlider
        ref={sliderRef}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        isHovered={isHovered}
        {...props}
      />
      <SliderTrackAfter
        isHovered={isHovered}
        trackWidth={trackWidth}
        disabled={disabled}
      />
    </SliderContainer>
  )
}

export default Slider
